import React from "react";
import { formatINR } from "@/lib/utils";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api/bill-audit`;

export const NAVY = "#0A1128";
export const GREEN = "#2F7D52";
export const AMBER = "#FFB703";
export const INK = "#1B2B22";
export const MUTE = "#64748B";

export const SEV = {
  high:   { label: "High",   bg: "#FEE2E2", fg: "#B91C1C", dot: "#DC2626" },
  medium: { label: "Medium", bg: "#FEF3C7", fg: "#92400E", dot: "#D97706" },
  low:    { label: "Low",    bg: "#DBEAFE", fg: "#1E40AF", dot: "#2563EB" },
  info:   { label: "Info",   bg: "#F1F5F9", fg: "#475569", dot: "#94A3B8" },
};

export const STATUS = {
  CONFIRMED:  { bg: "#DCFCE7", fg: "#166534" },
  PARTIAL:    { bg: "#FEF3C7", fg: "#92400E" },
  UNVERIFIED: { bg: "#FEE2E2", fg: "#B91C1C" },
};

export const SLOT_COLORS = { realtime: "#2F7D52", banked: "#FFB703", bank_used: "#F59E0B", lapsed: "#DC2626", export: "#60A5FA", grid: "#CBD5E1", gen: "#0A1128" };

export const num = (v, dec = 0) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: dec, minimumFractionDigits: dec }));
export const kwh = (v) => (v === null || v === undefined ? "—" : `${num(v)} kWh`);
export const inr = (v) => formatINR(v);
export const rate = (v, dec = 2) => (v === null || v === undefined ? "—" : `₹${Number(v).toFixed(dec)}`);
export const pct = (v, dec = 1) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(dec)}%`);
export const monthLabel = (ym) => {
  if (!ym || ym.length < 7) return ym || "—";
  const d = new Date(`${ym}-01T00:00:00`);
  return Number.isNaN(d.getTime()) ? ym : d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
};

export function Badge({ children, bg = "#F1F5F9", fg = "#475569", style, ...rest }) {
  return (
    <span {...rest} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: bg, color: fg, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap", ...style }}>
      {children}
    </span>
  );
}

export function SevBadge({ sev }) {
  const s = SEV[sev] || SEV.info;
  return <Badge bg={s.bg} fg={s.fg}><span style={{ width: 6, height: 6, borderRadius: 3, background: s.dot }} />{s.label}</Badge>;
}

export function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.PARTIAL;
  return <Badge bg={s.bg} fg={s.fg} title="Confidence of the regulatory input behind this figure">{status || "PARTIAL"}</Badge>;
}

export function Card({ children, style, ...rest }) {
  return (
    <div {...rest} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 14, padding: 16, boxShadow: "0 1px 2px rgba(16,24,40,0.04)", ...style }}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, right, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, color: INK, letterSpacing: "0.02em" }}>{children}</div>
        {sub && <div style={{ fontSize: 11.5, color: MUTE, marginTop: 2 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

export const btn = (variant = "primary") => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid transparent",
  ...(variant === "primary" ? { background: GREEN, color: "#fff" }
    : variant === "dark" ? { background: NAVY, color: AMBER }
    : variant === "danger" ? { background: "#FEF2F2", color: "#DC2626", borderColor: "#FCA5A5" }
    : { background: "#fff", color: INK, borderColor: "#CBD5E1" }),
});

export const inp = { width: "100%", padding: "7px 9px", borderRadius: 6, border: "1px solid #CBD5E1", fontSize: 12, background: "#fff", fontFamily: "inherit" };
export const lab = { display: "block", fontSize: 10, fontWeight: 800, color: MUTE, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 };

/** Big KPI tile used on the portfolio and the consumer dashboard. */
export function Kpi({ label, value, sub, accent = GREEN, dark = false, testId }) {
  return (
    <div data-testid={testId} style={{ position: "relative", overflow: "hidden", borderRadius: 14, padding: "14px 16px", background: dark ? "rgba(255,255,255,0.06)" : "#fff", border: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E2E8F0", minWidth: 0 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent }} />
      <div style={{ fontSize: 10.5, fontWeight: 800, color: dark ? "rgba(255,255,255,0.65)" : MUTE, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: dark ? "#fff" : INK, marginTop: 6, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.6)" : MUTE, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export function Empty({ icon: Icon, title, sub, children }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px", color: MUTE }}>
      {Icon && <Icon size={36} style={{ margin: "0 auto 10px", opacity: 0.4 }} />}
      <div style={{ fontSize: 14, fontWeight: 700, color: INK }}>{title}</div>
      {sub && <div style={{ fontSize: 12, marginTop: 4, maxWidth: 420, margin: "4px auto 0" }}>{sub}</div>}
      {children && <div style={{ marginTop: 14 }}>{children}</div>}
    </div>
  );
}

export const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
  return e?.message || fallback;
};

/** In-app modal (the Claude browser pane and some kiosks suppress window.confirm, so never rely on it). */
export function Modal({ title, onClose, children, width = 440, testId }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,17,40,0.45)", zIndex: 60, display: "grid", placeItems: "center", padding: 16 }} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: width, boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #EEF2F7", fontSize: 14, fontWeight: 800, color: INK }}>{title}</div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

export function ConfirmDialog({ title, body, confirmLabel = "Delete", onConfirm, onClose, busy, testId = "confirm-dialog" }) {
  return (
    <Modal title={title} onClose={onClose} testId={testId}>
      <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.5 }}>{body}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={btn("ghost")} data-testid={`${testId}-cancel`}>Cancel</button>
        <button onClick={onConfirm} disabled={busy} style={btn("danger")} data-testid={`${testId}-ok`}>{busy ? "Working…" : confirmLabel}</button>
      </div>
    </Modal>
  );
}
